import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Heart, Minus, Plus, Clock, Flame, Share2, Utensils } from "lucide-react";

export default function RecipeDetailPage() {
  const { recipeId } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [servings, setServings] = useState(1);

  useEffect(() => {
    api.get(`/recipes/${recipeId}`).then((r) => {
      setRecipe(r.data);
      setServings(r.data.servings || 1);
    });
  }, [recipeId]);

  const toggleFav = async () => {
    const { data } = await api.post(`/recipes/${recipeId}/favorite`);
    setRecipe((r) => ({ ...r, is_favorite: data.is_favorite }));
    toast.success(data.is_favorite ? "أُضيفت للمفضلة" : "أُزيلت من المفضلة");
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: recipe.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("تم نسخ الرابط"); }
    } catch {}
  };

  if (!recipe) return <div className="text-center text-muted-foreground py-12">جاري التحميل...</div>;
  const factor = servings / (recipe.servings || 1);

  return (
    <div className="space-y-6 max-w-4xl mx-auto lg:mx-0">
      <Link to="/recipes" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowRight className="h-4 w-4 rtl:-scale-x-100" /> العودة للوصفات
      </Link>
      <div className="grid md:grid-cols-2 gap-8">
        <img src={recipe.image_url} alt={recipe.title} className="w-full aspect-square rounded-2xl object-cover" />
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {(recipe.diet_tags || []).map((t) => (<Badge key={t} variant="secondary">{t}</Badge>))}
          </div>
          <h1 className="text-3xl font-display font-semibold" data-testid="recipe-title">{recipe.title}</h1>
          <p className="text-muted-foreground leading-relaxed">{recipe.short_description}</p>

          {recipe.fitness_note && (
            <div
              className="rounded-lg border border-emerald-200/70 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 p-3 flex items-start gap-2"
              data-testid="recipe-benefit-detail"
            >
              <span className="text-lg leading-none">{recipe.health_style === "strict" ? "💪" : "🥗"}</span>
              <div>
                <div className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 mb-0.5">
                  {recipe.health_style === "strict" ? "فائدة رياضية" : "فائدة صحية"}
                </div>
                <p className="text-sm text-emerald-800 dark:text-emerald-100 leading-relaxed">
                  {recipe.fitness_note}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 py-4">
            <StatMini icon={Clock} label="التحضير" value={`${recipe.prep_minutes}د`} />
            <StatMini icon={Utensils} label="الطبخ" value={`${recipe.cook_minutes}د`} />
            <StatMini icon={Flame} label="سعرات/حصة" value={recipe.calories_per_serving} />
          </div>

          <div className="flex gap-3">
            <Button onClick={toggleFav} variant={recipe.is_favorite ? "default" : "outline"} className="rounded-full gap-2" data-testid="btn-toggle-favorite">
              <Heart className={`h-4 w-4 ${recipe.is_favorite ? "fill-current" : ""}`} />
              {recipe.is_favorite ? "في المفضلة" : "إضافة للمفضلة"}
            </Button>
            <Button onClick={share} variant="outline" className="rounded-full gap-2" data-testid="btn-share">
              <Share2 className="h-4 w-4" /> مشاركة
            </Button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-semibold text-lg">المكونات</h2>
            <div className="flex items-center gap-2" data-testid="servings-adjuster">
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => setServings(Math.max(1, servings - 1))} data-testid="btn-decrease-servings"><Minus className="h-3 w-3" /></Button>
              <span className="min-w-[60px] text-center text-sm">{servings} حصص</span>
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => setServings(servings + 1)} data-testid="btn-increase-servings"><Plus className="h-3 w-3" /></Button>
            </div>
          </div>
          <ul className="space-y-2 text-sm">
            {(recipe.ingredients || []).map((ing, i) => (
              <li key={i} className="flex justify-between py-1.5 border-b last:border-0" data-testid={`ingredient-${i}`}>
                <span>{ing.name}</span>
                <span className="text-muted-foreground">{Math.round((ing.amount || 0) * factor * 100) / 100} {ing.unit}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="p-6">
          <h2 className="font-display font-semibold text-lg mb-4">خطوات التحضير</h2>
          <ol className="space-y-3 text-sm">
            {(recipe.steps || []).map((s, i) => (
              <li key={i} className="flex gap-3" data-testid={`step-${i}`}>
                <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs shrink-0">{i + 1}</span>
                <span className="leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="font-display font-semibold mb-4">المعلومات الغذائية (لكل حصة)</h3>
        <div className="grid grid-cols-4 gap-4 text-center">
          <NutMini label="السعرات" value={recipe.calories_per_serving} unit="كسع" />
          <NutMini label="البروتين" value={recipe.protein_g} unit="غم" />
          <NutMini label="الكربوهيدرات" value={recipe.carbs_g} unit="غم" />
          <NutMini label="الدهون" value={recipe.fat_g} unit="غم" />
        </div>
      </Card>
    </div>
  );
}

function StatMini({ icon: I, label, value }) {
  return (
    <div className="p-3 rounded-xl bg-secondary text-center">
      <I className="h-4 w-4 mx-auto text-primary" />
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
      <p className="font-display font-semibold">{value}</p>
    </div>
  );
}
function NutMini({ label, value, unit }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-display font-semibold text-lg">{value ?? "—"}</p>
      <p className="text-xs text-muted-foreground">{unit}</p>
    </div>
  );
}
